/* global alert, window */

import React from 'react'
import PropTypes from 'prop-types'
import { hashHistory } from 'react-router'

import * as firebase from 'firebase'
import { connect } from 'react-redux'

import PhoneNumberScreen from './PhoneNumberScreen'
import PinCodeScreen from './PinCodeScreen'
import CompanyScreen from './CompanyScreen'
import OnboardingStatusScreen from './OnboardingStatusScreen'

import { PhoneNumberUtils } from '@fibo-app/fibo-collab-shared'
import LoginUtils from 'infra/LoginUtils'
import TeamUtils from 'infra/TeamUtils'
import Analytics from 'infra/analytics/Analytics'

class OnboardingScreenManager extends React.Component {

  static propTypes = {
    dispatch: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      screen: 0,
      loading: false,
      phoneInfo: {
        countryCode: '+1',
        countryName: 'United States',
        number: ''
      },
      companyInfo: {},
      companyInfoValidation: null,
      pinCode: 0
    }
  }

  handlePhoneInfoChanged = phoneInfo => {
    this.setState({ phoneInfo })
  }

  handleCompanyInfoChanged = companyInfo => {
    this.setState({
      companyInfo,
      companyInfoValidation: null
    })
  }

  handlePinCodeChanged = pinCode => {
    this.setState({ pinCode })
  }

  handlePhoneNumberNextClicked = () => {
    const validation = PhoneNumberUtils.validateIntlNumber(
      this.state.phoneInfo.countryCode, this.state.phoneInfo.number)
    if (validation.errMessage) {
      alert(validation.errMessage)
    }
    else
    if (!validation.completeNumber) {
      alert('Error validating number.')
    }
    else {
      this.trySendSmsCode(validation.completeNumber)
    }
  }

  getUserMetadata = userId => {
    return firebase.database()
      .ref(`users/${userId}/metadata`)
      .once('value')
      .then(snapshot => snapshot && snapshot.val())
  }

  trySendSmsCode = completePhoneNumber => {
    this.setState({ loading: true })
    LoginUtils.sendSmsCode(completePhoneNumber)
      .then(response => {
        if (response && response.code) {
          this.setState({
            loading: false,
            completePhoneNumber,
            screen: this.state.screen + 1,
            securePinCode: parseInt(response.code)
          })
        }
      })
      .catch(() => {
        this.setState({ loading: false })
        alert('Invalid Phone Number or you are offline')
      })
  }

  /**
   * Returns true if the authenticated user is new to the system, false otherwise.
   *
   * Invited members have an entry in the users table, only the metadata path is filled
   * when the user onboards into the app.
   */
  getAuthenticatedUserState = () => {
    return firebase.database()
      .ref(`users/${this.state.completePhoneNumber}/metadata`)
      .once('value')
      .then(snapshot => !snapshot || !snapshot.val())
  }

  handlePinNextClicked = () => {
    if (this.state.securePinCode !== this.state.pinCode) {
      // TODO: Should use custom dialogs accross the app,
      // request designs.
      alert('Invalid code, please try again.')
    }
    else {
      this.setState({ loading: true })
      LoginUtils.loginWithCustomToken(this.state.completePhoneNumber)
        .then(() => {

          this.props.dispatch({
            type: 'ANALYTICS_UPDATE_INFO',
            info: { fullPhoneNumber: this.state.completePhoneNumber }
          })

          if (window.analytics){
            const properties = {
              country_code: this.state.phoneInfo.countryCode,
              phone_number_raw: this.state.phoneInfo.number,
              full_number: this.state.completePhoneNumber
            }
            //console.log(`Analytics dispatch track ${Analytics.LOGIN_SUCCESSFUL_TRACKER}, with props `, properties)
            window.analytics.track(Analytics.LOGIN_SUCCESSFUL_TRACKER, properties);
          }

          // Get the authenticated user to check if we need to
          // do the onboarding or redirect to the dashboard.
          return this.getAuthenticatedUserState()
        })
        .then(isNewUser => {
          this.setState({ loading: false })
          // Go to the next onboarding screen
          if (isNewUser) {
            this.setState({
              screen: this.state.screen + 1
            })
          }
          // Navigate to the app dashboard.
          else {
            Analytics.track(Analytics.DASHBOARD_SIGNED_IN)
            hashHistory.push('app/schedule')
          }
        })
        .catch(e => {
          console.error(e)
          this.setState({ loading: false })
          alert('Error while authenticating, please try again.', e)
        })
    }
  }

  handleCompanyNextClicked = () => {
    // Try creating the team with the company values.
    const companyInfo = this.state.companyInfo
    const companyInfoValidation = this.validateCompanyInfo(companyInfo)
    if (companyInfoValidation.success) {
      this.setState({ loading: true })
      TeamUtils.createTeam(companyInfo,
        this.state.completePhoneNumber, this.state.phoneInfo.countryName)
        .then(() => {
          this.setState({
            loading: false,
            screen: this.state.screen + 1
          })
        })
        .catch(() => {
          this.setState({ loading: false })
          alert('Error while creating the team, please try again or contact support.')
        })
    }
    else {
      this.setState({ companyInfoValidation })
    }
  }

  validateEmail = email => {
    var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(email.toLowerCase())
  }

  validateCompanyInfo = companyInfo => {
    const result = {}
    // All fields in the company screen are required
    if (!companyInfo.fullName) {
      result.fullName = {
        message: 'Your full name is required'
      }
    }
    if (!companyInfo.email) {
      result.email = {
        message: 'Your email is required'
      }
    }
    else if (!this.validateEmail(companyInfo.email)) {
      result.email = {
        message: 'Please enter a valid email address'
      }
    }
    if (!companyInfo.companyName) {
      result.companyName = {
        message: 'You organization name is required'
      }
    }
    if (!companyInfo.role) {
      result.role = {
        message: 'Your role in the team is required'
      }
    }
    if (Object.keys(result).length > 0) {
      return result
    }
    return { success: true }
  }

  handleStatusNextClicked = () => {
    hashHistory.push('app/messages')
  }

  render() {
    if (this.state.screen === 0) {
      return (
        <PhoneNumberScreen
          phoneInfo={this.state.phoneInfo}
          loading={this.state.loading}
          onPhoneInfoChanged={this.handlePhoneInfoChanged}
          onNextClicked={this.handlePhoneNumberNextClicked} />
      )
    }
    if (this.state.screen === 1) {
      return (
        <PinCodeScreen
          phoneNumber={this.state.completePhoneNumber}
          pinCode={this.state.pinCode}
          loading={this.state.loading}
          onPinCodeChanged={this.handlePinCodeChanged}
          onNextClicked={this.handlePinNextClicked} />
      )
    }
    if (this.state.screen === 2) {
      return (
        <CompanyScreen
          companyInfo={this.state.companyInfo}
          companyInfoValidation={this.state.companyInfoValidation}
          loading={this.state.loading}
          onCompanyInfoChanged={this.handleCompanyInfoChanged}
          onNextClicked={this.handleCompanyNextClicked} />
      )
    }
    if (this.state.screen === 3) {
      return (
        <OnboardingStatusScreen
          onNextClicked={this.handleStatusNextClicked} />
      )
    }
  }

}

export default connect(state => ({ route: state.route }))(OnboardingScreenManager)



// WEBPACK FOOTER //
// ./src/login/OnboardingScreenManager.js